// src/components/FaqAccordion.jsx
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { faqItems as faqData } from '../data/faq'; 

const FaqAccordion = ({ emitJsonLd = false, questions = null }) => {
  const qs = useMemo(
    () => (Array.isArray(questions) && questions.length ? questions : faqData),
    [questions]
  );

  const [openIndex, setOpenIndex] = useState(null);
  const itemRefs = useRef([]);
  const btnRefs = useRef([]);

  const prefersNoMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const toggleAccordion = useCallback((index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const id = `faq-${index}`;
      const base = `${window.location.pathname}${window.location.search}`;
      window.history.replaceState(null, '', `${base}#${id}`);
    }
  }, []);

  // 初回フェードイン
  useEffect(() => {
    if (prefersNoMotion) {
      itemRefs.current.forEach((el) => {
        if (el) {
          el.style.opacity = 1;
          el.style.transform = 'translateY(0)';
        }
      });
      return;
    }
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.style.opacity = 1;
            e.target.style.transform = 'translateY(0)';
          }
        }),
      { threshold: 0.3 }
    );
    const nodes = [...itemRefs.current];
    nodes.forEach((n) => n && obs.observe(n));
    return () => obs.disconnect();
  }, [prefersNoMotion]);

  // #faq-<index> があれば自動で開く
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const h = decodeURIComponent(window.location.hash || '').replace('#', '');
    if (h.startsWith('faq-')) {
      const idx = Number(h.split('-')[1]);
      if (!Number.isNaN(idx) && qs[idx]) {
        setOpenIndex(idx);
        setTimeout(() => {
          const el = document.getElementById(`faq-question-${idx}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            el.focus();
          }
        }, 50);
      }
    }
  }, [qs]);

  // FAQPage 構造化データ
  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: qs.map((q) => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: { '@type': 'Answer', text: q.answer },
      })),
    }),
    [qs]
  );

  // ↑/↓ で移動、Enter/Space で開閉
  const onKeyDown = (e, index) => {
    const max = qs.length - 1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = index >= max ? 0 : index + 1;
      btnRefs.current[next]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = index <= 0 ? max : index - 1;
      btnRefs.current[prev]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleAccordion(index);
    }
  };

  return (
    <>
      <Helmet>
        {emitJsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
        <style>{`
          @media (prefers-reduced-motion: reduce) {
            .faq-anim { transition: none !important; }
          }
        `}</style>
      </Helmet>

      <div style={styles.container} aria-label="よくある質問">
        {qs.map((item, index) => {
          const isOpen = openIndex === index;
          const contentId = `faq-answer-${index}`;
          const buttonId = `faq-question-${index}`;
          const maxH =
            typeof document !== 'undefined'
              ? `${(document.getElementById(contentId)?.scrollHeight || 600)}px`
              : '600px';

          return (
            <div
              key={index}
              id={`faq-${index}`}
              ref={(el) => (itemRefs.current[index] = el)}
              className="faq-anim"
              style={{
                ...styles.item,
                opacity: 0,
                transform: 'translateY(20px)',
                transition: `opacity .5s ${index * 0.04}s ease-out, transform .5s ${index * 0.04}s ease-out`,
              }}
            >
              <button
                ref={(el) => (btnRefs.current[index] = el)}
                id={buttonId}
                type="button"
                onClick={() => toggleAccordion(index)}
                onKeyDown={(e) => onKeyDown(e, index)}
                aria-expanded={isOpen}
                aria-controls={contentId}
                style={{ ...styles.question, ...(isOpen ? styles.questionActive : {}) }}
              >
                {item.question}
                <span style={styles.icon} aria-hidden="true">
                  {isOpen ? '−' : '+'}
                </span>
              </button>

              <div
                id={contentId}
                role="region"
                aria-labelledby={buttonId}
                style={{
                  ...styles.answerWrapper,
                  maxHeight: isOpen ? maxH : '0px',
                  opacity: isOpen ? 1 : 0,
                  transition: prefersNoMotion ? 'none' : 'max-height .35s ease, opacity .35s ease',
                }}
              >
                <div style={styles.answer}>{item.answer}</div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

/* ====== styles ====== */
const styles = {
  container: {
    backgroundColor: '#fff0f5',
    padding: '16px',
    borderRadius: '12px',
    boxShadow: '0 6px 12px rgba(0,0,0,0.08)',
    maxWidth: '100%',
  },
  item: { marginBottom: '12px', willChange: 'transform, opacity' },
  question: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '15px',
    background: 'linear-gradient(135deg, #ffe6f0, #fff)',
    color: '#5d1049',
    border: '1px solid #ffc0cb',
    borderRadius: '10px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 600,
    transition: 'background .3s ease',
  },
  questionActive: { background: 'linear-gradient(135deg, #ffd6e0, #ffeef3)' },
  icon: { fontSize: '20px', marginLeft: '8px', color: '#e91e63' },
  answerWrapper: { overflow: 'hidden' },
  answer: {
    marginTop: '8px',
    padding: '14px 16px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #f8c8d8',
    fontSize: '14.5px',
    lineHeight: '1.8',
    color: '#555',
  },
};

export default FaqAccordion;
